/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import React from 'react';
import { Box, Text, Flex, Button, Spinner } from '@chakra-ui/react';
import {
  readAppliedTestContext,
  type AppliedTestContext,
  type QuickTestResult,
  type TestRunContext,
} from '../../services/demand-source.service';
import type { DemandSource } from '../../types';
import type { RequestPreview } from '../../hooks/useRequestPreview';
import { CapturedRequestView, RequestResponseInspector } from '../shared/RequestResponseInspector';
import { TestOutcomeStrip } from './TestOutcomeStrip';
import { TestDiagnosisList } from './TestDiagnosisList';
import { deriveTestOutcome, diagnoseTest, readTestBody, type DiagnosisFix } from '../../utils/test-diagnosis';

interface QuickTestPanelProps {
  sourceId: string | number;
  sourceName: string;
  result: QuickTestResult | null;
  isLoading: boolean;
  error: Error | null;
  /** Called with the surface's *Test as* choice, {@link QuickTestPanelProps.testContext}. */
  onTest: (id: string | number, context: TestRunContext) => void;
  /**
   * **The surface's one *Test as* choice** (`#979`, `useTestContext`). The panel held its own, so the
   * test ran as the device picked here while the row marks and the response preview beside it ran
   * as the default.
   */
  testContext: TestRunContext;
  /**
   * The picker, where this panel is where the surface shows it, and `null` where the surface shows
   * it somewhere else (the detail page puts it beside Test, where it is visible before a test has
   * run). Never both: two pickers over one choice are two controls over one slot.
   */
  contextPicker: React.ReactNode | null;
  /**
   * **Preview request** (`#979`): the request the rig would send, resolved by the real driver with
   * nothing sent and nothing recorded. `describes` says which configuration that is — the form, or
   * the page with its unsaved edits — because on the detail page it is not the one a test runs.
   */
  preview: { request: RequestPreview; describes: string } | null;
  /**
   * The configuration has changed since the result on screen was produced (`#947`). A result carried
   * from one page to the next is a claim about the configuration it ran on, and presenting it as
   * current is the staleness the preview already refuses (R4e). `null` where the surface cannot tell,
   * which renders nothing, as `false` does, but does not claim the result is current.
   */
  resultIsStale: boolean | null;
  onClose: () => void;
  /**
   * The configuration under test — the wizard's live form, or the saved row.
   * The diagnosis reads it to tell a mapping gap from a runtime failure; without
   * it the panel still reports the outcome and whatever the wire shows.
   */
  source?: Partial<DemandSource> | null;
  /** Stages an edit for a diagnosis Fix. Omitted where no editor is open. */
  onFix?: (fix: DiagnosisFix) => void;
}

/**
 * Inline panel displayed below a demand-source row after a quick test is
 * triggered.
 *
 * Order is claim, remedy, proof (#895 · R3, Fig. 9): the three-part outcome,
 * then what bidBrain thinks is wrong with a Fix each, then the evidence. On any
 * result that did not fill, the evidence is **always expanded** — the debug
 * toggle is retired there for two reasons, the second commercial. A diagnosis
 * can be wrong, and an operator who disagrees needs the payload in the same
 * view; and the constructed request, the real headers and the exchange's own
 * response are the proof the test actually ran. Hiding them hides the argument.
 *
 * On a fill there is no argument to make, so the evidence stays behind a toggle.
 */
export const QuickTestPanel: React.FC<QuickTestPanelProps> = ({
  sourceId,
  sourceName,
  result,
  isLoading,
  error,
  onTest,
  onClose,
  source,
  onFix,
  testContext,
  contextPicker,
  preview,
  resultIsStale,
}) => {
  const [showEvidence, setShowEvidence] = React.useState(false);
  const [showPreview, setShowPreview] = React.useState(false);

  // The server derives the outcome and persists the matching readiness (#897), so
  // render what it observed. The local derivation covers only the case the server
  // cannot report — the admin API itself never answered.
  const outcome = React.useMemo(
    () => result?.outcome ?? deriveTestOutcome({ result, requestError: error, source }),
    [result, error, source],
  );
  const causes = React.useMemo(() => diagnoseTest({ result, requestError: error, source }), [result, error, source]);

  const facts = readTestBody(result);
  const capture = facts.capture;
  const filled = outcome.filled.state === 'ok';

  // The simulator device/title the macros resolved against (#879). Without this
  // the operator can't tell a broken template from an empty test context.
  const applied = readAppliedTestContext(result);

  // Raw-body fallback for responses without a capture (e.g. a driver failure that
  // errored before the request was constructed).
  const bodyString =
    !capture && result?.body
      ? typeof result.body === 'string'
        ? result.body
        : JSON.stringify(result.body, null, 2)
      : null;

  const isVastXml = typeof result?.body === 'string' && result.body.trim().startsWith('<');

  const hasAttempted = Boolean(result || error);
  const evidenceVisible = capture != null && (!filled || showEvidence);

  const borderColor = isLoading
    ? 'accent.solid'
    : !hasAttempted
      ? 'border.default'
      : outcome.summary.tone === 'success'
        ? 'success.solid'
        : outcome.summary.tone === 'warning'
          ? 'warning.solid'
          : 'error.solid';

  return (
    <Box
      mt={2}
      p={4}
      bg="bg.card"
      borderWidth="1.5px"
      borderStyle="solid"
      borderColor={borderColor}
      borderRadius="8px"
      boxShadow="md"
    >
      <Flex align="center" justify="space-between" mb={3} gap={3} wrap="wrap">
        <Text fontWeight="600" fontSize="sm" color="fg.title">
          Quick Test — {sourceName}
        </Text>
        <Flex gap={2} align="center">
          {contextPicker}
          <Button
            size="xs"
            variant="outline"
            borderColor="accent.solid"
            color="accent.solid"
            _hover={{ bg: 'accent.subtle' }}
            onClick={() => onTest(sourceId, testContext)}
            disabled={isLoading}
          >
            {isLoading ? 'Running…' : hasAttempted ? 'Re-run' : 'Run Test'}
          </Button>
          {preview && (
            <Button
              size="xs"
              variant="outline"
              borderColor="border.default"
              color="fg.default"
              aria-pressed={showPreview}
              onClick={() => {
                // The rows' preview usually holds a resolution already; ask only when it does not.
                if (!showPreview && !preview.request.capture && !preview.request.stale) preview.request.refresh();
                setShowPreview((v) => !v);
              }}
            >
              {showPreview ? 'Hide preview' : 'Preview request'}
            </Button>
          )}
          <Button size="xs" variant="outline" borderColor="border.default" color="fg.muted" onClick={onClose}>
            Close
          </Button>
        </Flex>
      </Flex>

      {preview && showPreview && <RequestPreviewView preview={preview} />}

      {!isLoading && hasAttempted && resultIsStale && (
        <Text fontSize="xs" color="warning.ink" mb={2}>
          The configuration has changed since this test ran, so this result describes the earlier one. Re-run to test
          what is on screen now.
        </Text>
      )}

      {isLoading && (
        <Flex align="center" gap={3} py={2}>
          <Spinner size="sm" color="accent.solid" />
          <Text fontSize="sm" color="fg.muted">
            Sending test request to demand source…
          </Text>
        </Flex>
      )}

      {!isLoading && hasAttempted && (
        <Box>
          {/* 1 · The claim — what was observed, decomposed. */}
          <TestOutcomeStrip outcome={outcome} />

          {/* 2 · The remedy — what we think is wrong, ranked, with a Fix. */}
          <TestDiagnosisList causes={causes} onFix={onFix} />

          {/* 3 · The proof — the request as sent and the response as received. */}
          {capture && (
            <Box mt={3}>
              <Flex align="baseline" justify="space-between" gap={2} mb={2}>
                <Text fontSize="xs" fontWeight="700" color="fg.muted" letterSpacing="0.06em" textTransform="uppercase">
                  Request sent · Response received
                </Text>
                {filled && (
                  <Button size="xs" variant="ghost" color="accent.solid" onClick={() => setShowEvidence((v) => !v)}>
                    {showEvidence ? 'Hide detail' : 'Show detail'}
                  </Button>
                )}
              </Flex>

              {applied && <AppliedTestContextLine applied={applied} />}

              {evidenceVisible && (
                <Box p={3} bg="bg.canvas" borderRadius="8px" borderWidth="1px" borderColor="border.default">
                  <RequestResponseInspector capture={capture} producedAds={facts.ads} />
                </Box>
              )}
            </Box>
          )}

          {/* No capture at all — the driver failed before it built a request. */}
          {bodyString && (
            <Box mt={3} bg="bg.canvas" borderRadius="6px" p={3} maxH="220px" overflowY="auto">
              <Text fontSize="xs" color="fg.default" fontFamily="mono" whiteSpace="pre-wrap" wordBreak="break-all">
                {isVastXml ? bodyString.slice(0, 800) + (bodyString.length > 800 ? '\n… (truncated)' : '') : bodyString}
              </Text>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

/**
 * **What the macros resolved against** (#879), and whether the address was one an exchange accepts
 * (#927). Shared by a test and a preview, because both answer the same question about the rig.
 */
const AppliedTestContextLine: React.FC<{ applied: AppliedTestContext }> = ({ applied }) => (
  <>
    <Text fontSize="xs" color="fg.muted" mb={2}>
      Macros resolved against{' '}
      <Text as="span" fontWeight="600" color="fg.default">
        {applied.device?.name}
      </Text>
      {applied.content?.title ? (
        <>
          {' · '}
          <Text as="span" fontWeight="600" color="fg.default">
            {applied.content.title}
          </Text>
        </>
      ) : null}
      {applied.ip?.address ? (
        <>
          {' · IP '}
          <Text as="span" fontWeight="600" color="fg.default" title={applied.ip.note}>
            {applied.ip.address}
          </Text>
        </>
      ) : null}
      {applied.pod?.maxAds ? (
        <>
          {' · asking for '}
          <Text as="span" fontWeight="600" color="fg.default">
            {applied.pod.maxAds} ads
            {applied.pod.maxDuration ? ` / ${applied.pod.maxDuration}s` : ''}
          </Text>
        </>
      ) : null}
    </Text>
    {/* A non-routable address means the exchange declined to bid on the harness, not on the
        source. Saying so is the whole point — silence here is what made a rig limitation look like
        a configuration fault. */}
    {applied.ip && applied.ip.routable === false && applied.ip.note && (
      <Text fontSize="xs" color="warning.ink" mb={2}>
        {applied.ip.note}
      </Text>
    )}
  </>
);

/**
 * **The request, resolved and not sent** (`#979`). Read from the same resolution the rows' marks read,
 * so the panel and the rows cannot describe two different requests. Stale renders as resolving, never
 * as the previous request (R4e).
 */
const RequestPreviewView: React.FC<{ preview: { request: RequestPreview; describes: string } }> = ({ preview }) => {
  const { capture, applied, stale } = preview.request;
  return (
    <Box mb={3} p={3} bg="bg.canvas" borderRadius="8px" borderWidth="1px" borderColor="border.default">
      <Text fontSize="xs" fontWeight="700" color="fg.muted" letterSpacing="0.06em" textTransform="uppercase" mb={1}>
        Request preview · not sent
      </Text>
      <Text fontSize="xs" color="fg.muted" mb={2}>
        {preview.describes} Nothing is sent to the demand source and nothing is recorded.
      </Text>
      {stale ? (
        <Flex align="center" gap={2}>
          <Spinner size="xs" color="accent.solid" />
          <Text fontSize="xs" color="fg.muted">
            Resolving…
          </Text>
        </Flex>
      ) : capture ? (
        <>
          {applied && <AppliedTestContextLine applied={applied} />}
          <CapturedRequestView request={capture.request} title="Request that would be sent" />
        </>
      ) : (
        <Text fontSize="xs" color="fg.muted">
          No request resolved. Either the source has no endpoint yet, or the resolve failed.
        </Text>
      )}
    </Box>
  );
};
